import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Upload, Image as ImageIcon } from "lucide-react";
import api, { apiError } from "@/lib/api";

const IMG_URL = `${process.env.REACT_APP_BACKEND_URL}/api/news-popup/image`;

export default function AdminNews() {
  const [info, setInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/news-popup").then(({ data }) => setInfo(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("Image too large — keep it under 3 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/admin/news-popup/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("News image updated");
      await load();
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggle = async (enabled) => {
    setSaving(true);
    try {
      await api.post("/admin/news-popup/toggle", { enabled });
      toast.success(enabled ? "Popup enabled" : "Popup disabled");
      await load();
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const enabled = !!info?.enabled;
  const hasImage = !!info?.has_image;

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-news-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 grid place-items-center">
          <Megaphone className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h1 className="font-display font-extrabold text-xl">News Popup</h1>
          <p className="text-sm text-slate-400">Show an announcement image to members on their dashboard.</p>
        </div>
      </div>

      <div className="rounded-2xl bg-[#0D0E12] border border-amber-500/15 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-sm">Popup status</div>
            <div className="text-xs text-slate-400 mt-1">
              {enabled ? "Members see the popup once per session." : "The popup is currently hidden from members."}
            </div>
          </div>
          <button
            data-testid="news-toggle-btn"
            disabled={saving || (!hasImage && !enabled)}
            onClick={() => toggle(!enabled)}
            className={`relative w-14 h-7 rounded-full transition-all ${enabled ? "bg-emerald-500" : "bg-slate-700"} disabled:opacity-40`}
            title={!hasImage && !enabled ? "Upload an image first" : ""}
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${enabled ? "translate-x-7" : ""}`} />
          </button>
        </div>

        <div className="border-t border-amber-500/10 pt-5">
          <div className="font-semibold text-sm mb-3">Current image</div>
          <div className="rounded-xl border border-amber-500/15 bg-[#08080C] grid place-items-center overflow-hidden min-h-[180px]">
            {hasImage ? (
              <img
                data-testid="news-current-image"
                src={`${IMG_URL}?v=${info?.image_id || ""}`}
                alt="News popup"
                className="max-h-[360px] w-auto object-contain"
              />
            ) : (
              <div className="flex flex-col items-center gap-2 text-slate-500 py-10">
                <ImageIcon className="w-8 h-8" />
                <span className="text-sm">No image uploaded yet</span>
              </div>
            )}
          </div>

          <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="news-file-input" />
          <button
            data-testid="news-upload-btn"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="mt-4 inline-flex items-center gap-2 gold-btn px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50"
          >
            <Upload className="w-4 h-4" />
            {uploading ? "Uploading…" : hasImage ? "Replace image" : "Upload image"}
          </button>
          <p className="text-xs text-slate-500 mt-2">PNG, JPG or WEBP up to 3 MB. Landscape images look best.</p>
        </div>
      </div>
    </div>
  );
}
